import { useState } from 'react';
import {
  MAX_PRICE,
  MIN_PRICE,
  PRICE_MESSAGE_KEY,
  isPriceOutOfBounds,
  priceMessageParams,
} from '~/utils/moneyBounds';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui/buttons/Button';
import { CurrencySelector } from '~/components/ui/inputs/CurrencySelector';
import { Input } from '~/components/ui/inputs/Input';
import { TextArea } from '~/components/ui/inputs/TextArea';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import {
  useCreateShippingAddOn,
  useUpdateShippingAddOn,
  useGetShippingAddOnCategories,
} from '~/api/logistics';
import type {
  GetShippingAddOnsQuery,
  AddOnBillingUnit,
  Currency,
} from '~/graphql/graphql';
import { type ClientError, extractGraphQLErrorMessages } from '~/utils';

type ShippingAddOn = GetShippingAddOnsQuery['shippingAddOns'][number];

export type AddOnModalMode = 'create' | 'edit';

interface AddOnCostModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: AddOnModalMode;
  addOn?: ShippingAddOn;
}

const BILLING_UNIT_OPTIONS: AddOnBillingUnit[] = [
  'CONTAINER',
  'BL',
  'PERCENT_MATERIAL_VALUE',
  'BL_STD_CONTAINER',
  'CONTAINER_EQUIV',
  'TRUCKLOAD',
  'FLATRACK_CONTAINER',
  'BASE_UOM',
];

interface FormState {
  name: string;
  description: string;
  category: string;
  billingUnit: string;
  cost: string;
  currency: string;
  active: string;
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  category: '',
  billingUnit: '',
  cost: '',
  currency: 'USD',
  active: 'true',
});

const formFromAddOn = (addOn: ShippingAddOn): FormState => ({
  name: addOn.name,
  description: addOn.description ?? '',
  category: addOn.category,
  billingUnit: addOn.billingUnit,
  cost: String(addOn.cost),
  currency: addOn.currency,
  active: String(addOn.active),
});

export function AddOnCostModal({
  isOpen,
  onClose,
  mode,
  addOn,
}: AddOnCostModalProps) {
  const { t } = useTranslation('logistics');
  const [form, setForm] = useState<FormState>(() =>
    mode === 'edit' && addOn ? formFromAddOn(addOn) : emptyForm()
  );

  const { mutate: createAddOn, isPending: isCreating } =
    useCreateShippingAddOn();
  const { mutate: updateAddOn, isPending: isUpdating } =
    useUpdateShippingAddOn();
  const { data: categoriesData } = useGetShippingAddOnCategories();

  const isPending = isCreating || isUpdating;
  const categories = categoriesData?.shippingAddOnCategories ?? [];

  const set = (field: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const validate = (): boolean => {
    if (!form.name || !form.category || !form.billingUnit || !form.cost) {
      toast.error(t('logistics.common.required_fields_error'));
      return false;
    }
    if (parseFloat(form.cost) < 0) {
      toast.error(t('logistics.common.negative_value_error'));
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const costValue = parseFloat(form.cost);
    if (isNaN(costValue)) {
      toast.error(t('logistics.common.required_fields_error'));
      return;
    }
    if (isPriceOutOfBounds(costValue)) {
      toast.error(t(PRICE_MESSAGE_KEY, priceMessageParams));
      return;
    }

    const onSuccess = () => {
      toast.success(
        t(
          mode === 'create'
            ? 'logistics.add_on_costs.modal.success_add'
            : 'logistics.add_on_costs.modal.success_edit'
        )
      );
      onClose();
    };

    const onError = (error: ClientError) =>
      toast.error(extractGraphQLErrorMessages(error).join(', '));

    if (mode === 'edit' && addOn) {
      updateAddOn(
        {
          id: addOn.id,
          input: {
            name: form.name || undefined,
            description: form.description || undefined,
            category: form.category || undefined,
            billingUnit: (form.billingUnit as AddOnBillingUnit) || undefined,
            cost: {
              amount: costValue,
              currency: (form.currency || 'USD') as Currency,
            },
            active: form.active === 'true',
          },
        },
        { onSuccess, onError }
      );
    } else {
      createAddOn(
        {
          input: {
            name: form.name,
            description: form.description || undefined,
            category: form.category,
            billingUnit: form.billingUnit as AddOnBillingUnit,
            cost: {
              amount: costValue,
              currency: (form.currency || 'USD') as Currency,
            },
            active: form.active === 'true',
          },
        },
        { onSuccess, onError }
      );
    }
  };

  const titleKey = mode === 'create' ? 'add_title' : 'edit_title';
  const submitKey = mode === 'create' ? 'submit_add' : 'submit_edit';

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-xl">
      <div className="flex flex-col gap-6 p-8 bg-white rounded-lg">
        <h2 className="text-lg font-semibold text-neut-prim">
          {t(`logistics.add_on_costs.modal.${titleKey}`)}
        </h2>

        <section className="flex flex-col gap-4">
          <h3 className="text-sm font-medium text-neut-600 uppercase tracking-wide">
            {t('logistics.add_on_costs.modal.details_section')}
          </h3>

          <Input
            label={`${t('logistics.add_on_costs.modal.name')} *`}
            name="name"
            value={form.name}
            onChange={(_name, value) => set('name', value)}
          />
          <TextArea
            label={t('logistics.add_on_costs.modal.description')}
            name="description"
            value={form.description}
            onChange={(_name, value) => set('description', value)}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SearchableSelect
              label={`${t('logistics.add_on_costs.modal.type')} *`}
              name="category"
              value={form.category}
              onChange={(_name, value) => set('category', value)}
              options={categories.map((c) => ({ value: c, label: c }))}
              creatable
            />
            <SearchableSelect
              label={`${t('logistics.add_on_costs.modal.billing_unit')} *`}
              name="billingUnit"
              value={form.billingUnit}
              onChange={(_name, value) => set('billingUnit', value)}
              options={BILLING_UNIT_OPTIONS.map((o) => ({
                value: o,
                label: t(`logistics.add_on_costs.billing_unit.${o}`),
              }))}
            />
            <SearchableSelect
              label={t('logistics.add_on_costs.modal.active')}
              name="active"
              value={form.active}
              onChange={(_name, value) => set('active', value)}
              options={[
                {
                  value: 'true',
                  label: t('logistics.add_on_costs.active.true'),
                },
                {
                  value: 'false',
                  label: t('logistics.add_on_costs.active.false'),
                },
              ]}
            />
            <Input
              label={`${t('logistics.add_on_costs.modal.cost')} *`}
              name="cost"
              type="number"
              min={MIN_PRICE}
              max={MAX_PRICE}
              value={form.cost}
              onChange={(_name, value) => set('cost', value)}
            />
            <CurrencySelector
              label={t('logistics.common.currency')}
              name="currency"
              value={form.currency}
              onChange={(_name, value) => set('currency', value)}
            />
          </div>
        </section>

        <div className="flex justify-end gap-3 pt-2 border-t border-neut-border">
          <Button onClick={onClose} disabled={isPending}>
            {t('logistics.common.cancel')}
          </Button>
          <Button onClick={handleSubmit} loading={isPending}>
            {t(`logistics.add_on_costs.modal.${submitKey}`)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
